/**
 * Policy API Client
 * Provides authenticated access to the Policy API endpoints.
 * Uses the candidate app's dual-auth MSAL pattern (B2C / Corporate).
 */

import { IPublicClientApplication, AccountInfo, SilentRequest } from '@azure/msal-browser';
import { apiConfig, getLoginRequest, getAuthProvider, isTestMode } from '../../config';
import type {
    PolicyArtifact,
    PolicySearchRequest,
    PolicyFilterValues,
    PagedResult,
} from '../../types/policy';

/** Must match the upper bound enforced by PolicyController.SearchPolicies. */
const MAX_API_PAGE_SIZE = 100;
/** Safety stop so a misbehaving API can't cause an unbounded fetch loop. */
const MAX_FETCH_PAGES = 20;

interface ApiClientConfig {
    getAuthToken: () => Promise<string>;
    baseUrl: string;
}

function createConfig(
    msalInstance?: IPublicClientApplication | null,
    account?: AccountInfo | null
): ApiClientConfig {
    return {
        baseUrl: apiConfig.baseUrl,
        getAuthToken: async (): Promise<string> => {
            if (isTestMode || !msalInstance || !account) {
                return 'test-mock-token';
            }
            const authProvider = getAuthProvider();
            const loginReq = getLoginRequest(authProvider);
            const silentRequest: SilentRequest = {
                scopes: loginReq.scopes || [],
                account,
                forceRefresh: false,
            };
            const response = await msalInstance.acquireTokenSilent(silentRequest);
            return response.accessToken || response.idToken;
        },
    };
}

/** Thrown for non-2xx API responses so callers can branch on the HTTP status. */
export class PolicyApiError extends Error {
    constructor(public readonly status: number, statusText: string) {
        super(`API error: ${status} ${statusText}`);
        this.name = 'PolicyApiError';
    }
}

async function fetchWithAuth(
    config: ApiClientConfig,
    path: string,
    options: RequestInit = {}
): Promise<Response> {
    const token = await config.getAuthToken();
    const response = await fetch(`${config.baseUrl}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...options.headers,
        },
    });
    if (!response.ok) {
        throw new PolicyApiError(response.status, response.statusText);
    }
    return response;
}

/** Creates a policy API client with MSAL authentication. */
export function createPolicyApiClient(
    msalInstance?: IPublicClientApplication | null,
    account?: AccountInfo | null,
    impersonationEmail?: string | null
) {
    const config = createConfig(msalInstance, account);
    // When impersonating, search runs against the impersonated candidate's targeting profile.
    const searchPath = impersonationEmail
        ? `/api/policy/search/impersonate/${encodeURIComponent(impersonationEmail)}`
        : '/api/policy/search';

    const searchPolicies = async (
        request: PolicySearchRequest
    ): Promise<PagedResult<PolicyArtifact>> => {
        const response = await fetchWithAuth(config, searchPath, {
            method: 'POST',
            body: JSON.stringify(request),
        });
        return response.json();
    };

    return {
        /** Search policies. Targeting (company code, audience) is applied by the API. */
        searchPolicies,

        /**
         * Load every policy the signed-in (or impersonated) user is targeted for, so search,
         * sort, and paging can run client-side. Usually a single request; walks pages only if
         * the set exceeds the API's max page size.
         */
        getAllPolicies: async (): Promise<PolicyArtifact[]> => {
            const items: PolicyArtifact[] = [];
            for (let page = 1; page <= MAX_FETCH_PAGES; page++) {
                const result = await searchPolicies({ query: { page, pageSize: MAX_API_PAGE_SIZE } });
                items.push(...result.items);
                if (!result.hasNextPage) {
                    break;
                }
            }
            return items;
        },

        /** Get a single policy artifact by ID. */
        getPolicy: async (id: string): Promise<PolicyArtifact> => {
            const response = await fetchWithAuth(config, `/api/policy/${encodeURIComponent(id)}`);
            return response.json();
        },

        /** Fetch policy content through the API. Callers own the returned blob's lifetime. */
        getContent: async (id: string): Promise<{ blob: Blob; contentType: string }> => {
            const response = await fetchWithAuth(config, `/api/policy/${encodeURIComponent(id)}/content`);
            const blob = await response.blob();
            const contentType = (response.headers.get('Content-Type') ?? blob.type ?? '')
                .split(';')[0]
                .trim()
                .toLowerCase();
            return { blob, contentType };
        },

        /** Get available filter values. */
        getFilterValues: async (): Promise<PolicyFilterValues> => {
            const response = await fetchWithAuth(config, '/api/policy/filters');
            return response.json();
        },

        /** Get policy categories. */
        getCategories: async (): Promise<string[]> => {
            const response = await fetchWithAuth(config, '/api/policy/categories');
            return response.json();
        },
    };
}

export type PolicyApiClient = ReturnType<typeof createPolicyApiClient>;
